import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, CheckCircle, XCircle, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ManageAdvisors({ user, onLogout }) {
  const navigate = useNavigate();
  const [signups, setSignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending'); // pending, approved, rejected

  useEffect(() => {
    fetchSignups();
  }, [filter]);

  const fetchSignups = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/advisor-signups?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSignups(response.data.signups || []);
    } catch (error) {
      console.error('Error fetching signups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (advisorId) => {
    if (!window.confirm('Approve this advisor signup?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/admin/approve-advisor/${advisorId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Advisor approved!\n\nEmail: ${response.data.email}\nTemporary Password: ${response.data.temp_password}\n\nPlease send these credentials to the advisor.`);
      fetchSignups();
    } catch (error) {
      console.error('Error approving advisor:', error);
      alert('Error approving advisor. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen p-4 lg:p-8">
        <Navigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 lg:p-8">
      <Navigation user={user} onLogout={onLogout} />
      <div className="max-w-6xl mx-auto">
        
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-800 dark:text-gray-100 mb-2">
            Manage Advisor Signups
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Review and approve advisor registration requests
          </p>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          <Button
            variant={filter === 'pending' ? 'default' : 'outline'}
            onClick={() => setFilter('pending')}
          >
            <Clock className="w-4 h-4 mr-2" />
            Pending
          </Button>
          <Button
            variant={filter === 'approved' ? 'default' : 'outline'}
            onClick={() => setFilter('approved')}
          >
            <CheckCircle className="w-4 h-4 mr-2" />
            Approved
          </Button>
          <Button
            variant={filter === 'rejected' ? 'default' : 'outline'}
            onClick={() => setFilter('rejected')}
          >
            <XCircle className="w-4 h-4 mr-2" />
            Rejected
          </Button>
        </div>

        {/* Signups List */}
        {signups.length === 0 ? (
          <Card className="p-12 text-center">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-100 mb-2">
              No {filter} signups
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              There are no advisor signups with status: {filter}
            </p>
          </Card>
        ) : (
          <div className="space-y-4">
            {signups.map((signup) => (
              <Card key={signup.id} className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <span className="text-blue-600 dark:text-blue-400 font-bold text-lg">
                          {signup.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-800 dark:text-gray-100">
                          {signup.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {signup.company || 'Independent Advisor'}
                        </p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Email:</span>
                        <p className="font-medium text-gray-800 dark:text-gray-100">{signup.email}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Phone:</span>
                        <p className="font-medium text-gray-800 dark:text-gray-100">{signup.phone}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Plan:</span>
                        <p className="font-medium text-gray-800 dark:text-gray-100 capitalize">{signup.plan}</p>
                      </div>
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Registered:</span>
                        <p className="font-medium text-gray-800 dark:text-gray-100">
                          {new Date(signup.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {signup.status === 'pending' && (
                    <div className="flex gap-2 ml-4">
                      <Button
                        onClick={() => handleApprove(signup.id)}
                        className="bg-green-600 hover:bg-green-700"
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
